import styles from "./AprTable.module.scss";
import React, { ReactElement, useState } from "react";
import { ProviderInfo } from "../../models/ProviderInfo";
import { Flex, Group, Tooltip } from "@mantine/core";
import { EarningsData } from "../../models/EarningsData";
import { EarningsProtocolType } from "../../models/enums";
import {
  formatAddress,
  formatNumber,
  isMobileView,
  isOutlierYearlyApr,
} from "../../utils/formatter";
import CopyTextComponent from "../copyText/CopyText.component";
import { Validator } from "../../models/Validator";
import { Web3 } from "web3";
import { IconAlertTriangle, IconHelpSquareRounded } from "@tabler/icons-react";

export default function AprTable({
  providers,
  validators,
  earnings,
  type,
  registeredFtsoCount,
  embed,
}: {
  type: "ftso" | "stake" | "earnings";
  validators?: Validator[] | null;
  providers?: ProviderInfo[] | null;
  earnings?: EarningsData[] | null;
  registeredFtsoCount?: number;
  embed?: boolean;
}): ReactElement | null {
  const initialItemCount = embed ? 3 : 5;
  const [numberOfItems, setNumberOfItems] = useState<number>(initialItemCount);
  const web3 = new Web3();

  function ftsoOrderWarningCheck(
    order: number | null,
    registeredFtsos: number
  ): boolean {
    if (order === null) {
      return false;
    }
    //da je FTSO Provider v trenutnem epoch-u na mestih med 90 in 100 mestom po podatku Weight
    // (v kolikor jih je sploh 100 registriranih; v kolikor je samo 40 registriranih noben ne dobi sivega trikotnika)
    if (registeredFtsos < 100) {
      return false;
    }

    return order >= 90 && order <= 100;
  }

  const PROVIDER_NOT_REGISTERED =
    "FTSOv2 protocol: Provider is not registered in the current epoch and will not receive any rewards.";
  const PROVIDER_LOW_WEIGHT =
    "FTSOv2 protocol: Provider is among the last 10 registered providers by weight.";
  const NOT_REGISTEDRED_IN_LAST_EPOCH =
    "The FTSO data provider was not registered and did not receive any rewards in the last epoch.";
  const APR_OUTLIER =
    "The estimated APY for this provider is currently unreliable and is not shown.";

  const validatorsRows: JSX.Element[] =
    validators
      ?.map((validator: Validator) => (
        <Group key={validator.txID} wrap={"nowrap"} className={styles.tableRow}>
          <div className={styles.valValidatorCol}>
            <span>{validator.name ?? "/"}</span>
          </div>
          <div className={styles.valNodeIdCol}>
            <CopyTextComponent
              text={validator.nodeID ? formatAddress(validator.nodeID) : "/"}
              value={validator.nodeID ?? "/"}
              primaryColor={false}
              weight={"500"}
              size={"13px"}
              gap={isMobileView() ? "0" : "5"}
            />
          </div>
          <div className={styles.valFreeSpaceCol}>
            <span>
              {validator.roi
                ? `${formatNumber(validator.freeSpace, true, 0)}`
                : "No data"}
            </span>
          </div>
          <div className={styles.width15}>
            <span className={styles.aprValue}>
              {validator.roi
                ? `${formatNumber(validator.roi * 1e4, false, 2)}%`
                : "No data"}
            </span>
          </div>
        </Group>
      ))
      .slice(0, numberOfItems) ?? [];

  const ftsoRows: JSX.Element[] =
    providers
      ?.map((ftso: ProviderInfo) => (
        <Group
          key={ftso.address}
          wrap={"nowrap"}
          className={`${styles.tableRow} ${!ftso.registered ? styles.notRegistered : ""}`}
        >
          <Flex className={styles.width40} gap={"5px"} align={"center"}>
            <span>{ftso.name ?? "/"}</span>
            {(!ftso.registered ||
              ftsoOrderWarningCheck(ftso.order, registeredFtsoCount ?? 0)) && (
              <>
                <Tooltip
                  label={
                    !ftso.registered
                      ? PROVIDER_NOT_REGISTERED
                      : PROVIDER_LOW_WEIGHT
                  }
                  events={{ hover: true, focus: false, touch: true }}
                  maw={"320px"}
                  multiline
                  position="top"
                >
                  <IconAlertTriangle
                    size={"1rem"}
                    color={
                      !ftso.registered ? "var(--danger)" : "var(--mid-dark)"
                    }
                  />
                </Tooltip>
              </>
            )}
          </Flex>
          <div className={styles.width40}>
            <CopyTextComponent
              text={
                ftso.address
                  ? formatAddress(web3.utils.toChecksumAddress(ftso.address))
                  : "/"
              }
              value={
                ftso.address ? web3.utils.toChecksumAddress(ftso.address) : "/"
              }
              primaryColor={false}
              weight={"500"}
              size={"13px"}
              gap={isMobileView() ? "0" : "5"}
            />
          </div>
          <div className={styles.width20}>
            <span className={styles.aprValue}>
              {isOutlierYearlyApr(ftso.yearlyApr) ? (
                <Flex align={"center"} justify={"end"} gap={"5px"}>
                  <Tooltip
                    label={APR_OUTLIER}
                    events={{ hover: true, focus: false, touch: true }}
                    maw={"320px"}
                    multiline
                    position="top"
                  >
                    <IconAlertTriangle size={"1rem"} color={"var(--mid-dark)"} />
                  </Tooltip>
                  <span>N/A</span>
                </Flex>
              ) : (
                `${formatNumber(ftso.yearlyApr * 1e2, false, 2)}%`
              )}
            </span>
          </div>
        </Group>
      ))
      .slice(0, numberOfItems) ?? [];

  const earningsRows: JSX.Element[] =
    earnings
      ?.map((earning: EarningsData, index) => (
        <Group key={index} wrap={"nowrap"} className={styles.tableRow}>
          <div
            className={`${earning.protocol === EarningsProtocolType.NONE ? styles.noneProtocolCol : styles.protocolCol}`}
          >
            <span>
              {earning.protocol === EarningsProtocolType.NONE
                ? "Non-delegated or staked"
                : earning.protocol}
            </span>
          </div>
          {earning.protocol !== EarningsProtocolType.NONE && (
            <div className={styles.providerCol}>
              {earning.provider.length > 25 ? (
                <CopyTextComponent
                  text={formatAddress(earning.provider)}
                  value={earning.provider}
                  primaryColor={false}
                  weight={"500"}
                  size={"13px"}
                  gap={"0"}
                />
              ) : (
                <span>{earning.provider}</span>
              )}
            </div>
          )}

          <div className={styles.amountCol}>
            {formatNumber(earning.amount / 1e12, false, 2)}
          </div>
          <div className={styles.aprCol}>
            <span>
              {earning.protocol === EarningsProtocolType.NONE ? (
                "0%"
              ) : earning.apr !== null ? (
                formatNumber(earning.apr * 1e4, false, 2) + "%"
              ) : (
                <Flex align={"center"} justify={"end"} gap={"5px"}>
                  <Tooltip
                    label={
                      earning.aprUnavailableReason === "outlier"
                        ? APR_OUTLIER
                        : NOT_REGISTEDRED_IN_LAST_EPOCH
                    }
                    events={{ hover: true, focus: false, touch: true }}
                    maw={"320px"}
                    multiline
                    position="top"
                  >
                    <IconAlertTriangle
                      size={"1rem"}
                      color={
                        earning.aprUnavailableReason === "outlier"
                          ? "var(--mid-dark)"
                          : "var(--danger)"
                      }
                    />
                  </Tooltip>
                  <span>N/A</span>
                </Flex>
              )}
            </span>
          </div>
        </Group>
      ))
      .slice(0, numberOfItems) ?? [];

  const renderFTSOHeaders = (): JSX.Element => (
    <>
      <div className={`${styles.tableHeader} ${styles.width40}`}>
        <span>FTSO Provider</span>
      </div>
      <div className={`${styles.tableHeader} ${styles.width40}`}>
        <span>Address</span>
      </div>
      <div
        className={`${styles.tableHeader} ${styles.aprValue} ${styles.width20} ${styles.textWithHelpContainer}`}
      >
        <span>APY</span>
        <Tooltip
          label="Estimated annual percentage returns based on the latest known epoch rewards."
          position="bottom"
          maw={"320px"}
          multiline
          withArrow
          events={{
            hover: true,
            focus: false,
            touch: true,
          }}
        >
          <IconHelpSquareRounded className={styles.iconHelp} size={20} />
        </Tooltip>
      </div>
    </>
  );

  const renderValidatorHeaders = (): JSX.Element => (
    <>
      <div className={`${styles.tableHeader} ${styles.valValidatorCol}`}>
        <span>Validator</span>
      </div>
      <div className={`${styles.tableHeader} ${styles.valNodeIdCol}`}>
        <span>Node ID</span>
      </div>
      <div className={`${styles.tableHeader} ${styles.valFreeSpaceCol}`}>
        <span>Free space</span>
      </div>
      <div
        className={`${styles.tableHeader} ${styles.aprValue} ${styles.width15} ${styles.textWithHelpContainer}`}
      >
        <span>APY</span>
        <Tooltip
          label="Estimated annual percentage returns based on the latest known epoch rewards."
          position="bottom"
          maw={"320px"}
          multiline
          withArrow
          events={{
            hover: true,
            focus: false,
            touch: true,
          }}
        >
          <IconHelpSquareRounded className={styles.iconHelp} size={20} />
        </Tooltip>
      </div>
    </>
  );

  const renderEarningsHeaders = (): JSX.Element => (
    <>
      <div className={`${styles.tableHeader} ${styles.protocolCol}`}>
        <span>Protocol</span>
      </div>
      <div className={`${styles.tableHeader} ${styles.providerCol}`}>
        <span>Provider</span>
      </div>
      <div className={`${styles.tableHeader} ${styles.amountCol}`}>
        <span>Amount</span>
      </div>
      <div className={`${styles.tableHeader} ${styles.aprCol}`}>
        <span>APY</span>
      </div>
    </>
  );

  const TableHeaders = ({ type }: { type: string }): JSX.Element => {
    switch (type) {
      case "ftso":
        return renderFTSOHeaders();
      case "stake":
        return renderValidatorHeaders();
      default:
        return renderEarningsHeaders();
    }
  };

  const TableRows = ({ type }: { type: string }): JSX.Element => {
    switch (type) {
      case "ftso":
        return <div>{ftsoRows}</div>;
      case "stake":
        return <div>{validatorsRows}</div>;
      default:
        return <>{earningsRows}</>;
    }
  };

  const showMoreEnabled = (): boolean => {
    if (type === "stake") {
      return (validators?.length ?? 0) > numberOfItems;
    } else if (type === "ftso") {
      return (providers?.length ?? 0) > numberOfItems;
    } else {
      return false;
    }
  };

  const ViewAll = ({ type }: { type: string }): JSX.Element => {
    switch (type) {
      case "stake":
        return (
          <span
            onClick={() =>
              window.open(
                "https://flare.space/dapp/p-chain-explorer/validators"
              )
            }
          >
            View all Validators
          </span>
        );
      case "ftso":
        return (
          <span
            onClick={() =>
              window.open(
                `https://flare-systems-explorer.flare.network/providers`
              )
            }
          >
            View all Providers
          </span>
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <div className={styles.table}>
        <Group className={styles.tableRow} wrap={"nowrap"}>
          <TableHeaders type={type} />
        </Group>
        <TableRows type={type} />
        {type === "earnings" ? (
          showMoreEnabled() && (
            <Group className={styles.tableFooter}>
              <span
                onClick={() =>
                  setNumberOfItems((prevState) => prevState + initialItemCount)
                }
              >
                Show more
              </span>

              <ViewAll type={type} />
            </Group>
          )
        ) : (
          <Group className={styles.tableFooter}>
            {showMoreEnabled() && (
              <span
                onClick={() =>
                  setNumberOfItems((prevState) => prevState + initialItemCount)
                }
              >
                Show more
              </span>
            )}
            <ViewAll type={type} />
          </Group>
        )}
      </div>
    </>
  );
}
